import './index.scss';

import { Component, ReactNode } from 'react';
import parsePhoneNumberFromString, { CountryCode, formatIncompletePhoneNumber, getCountryCallingCode } from 'libphonenumber-js';

import data from './data';
import t from 'utils/i18n';
import { BraxUncontrolledTextField, BraxUncontrolledTextFieldProps } from '../BraxUncontrolledTextField';
import { BraxUncontrolledCheckboxField, BraxUncontrolledCheckboxFieldProps } from '../BraxUncontrolledCheckboxField';
import { BraxSelectOption, BraxUncontrolledSelectField } from '../BraxUncontrolledSelectField';

type PhoneCode = CountryCode | '+800' | '+808' | '+870' | '+878' | '+881' | '+882' | '+883' | '+888' | '+979'

export type BraxUncontrolledPhoneFieldProps = Omit<BraxUncontrolledTextFieldProps, 'onChange'|'type' | 'mask' | 'maxLength'> & {
    whatsapp?: boolean | BraxUncontrolledCheckboxFieldProps | (() => ReactNode),
    onChange?: (value: string) => void
};

type BraxUncontrolledPhoneFieldState = {
    value: string,
    code?: PhoneCode
};

export class BraxUncontrolledPhoneField extends Component<BraxUncontrolledPhoneFieldProps, BraxUncontrolledPhoneFieldState> {

    static defaultProps = {
        disabled: false,
        status: null,
        value: '',
        whatsapp: true
    };

    state = this.props.value ? this.parseValueAsPhone(this.props.value) : { value: '+55 ', code: 'BR' as PhoneCode };

    codes = Object.keys(data) as PhoneCode[];

    codesOptions: BraxSelectOption<PhoneCode>[] = this.codes.map(code => ({ title: data[code], value: code }));

    componentDidUpdate(prevProps: BraxUncontrolledPhoneFieldProps) {
        if (prevProps.value !== this.props.value && this.props.value !== this.state.value)
            this.change(this.props.value || '');
    }

    parseValueAsPhone(phone: string, code?: PhoneCode) {
        if (!phone)
            return { value: '+', code: code };

        if (code)
            phone = phone.replace(/^\+[0-9]*/, code[0] === '+' ? code : `+${getCountryCallingCode(code as CountryCode)}`);
        else
            if (phone[0] !== '+') {
                const parsed = parsePhoneNumberFromString(phone, 'BR');

                phone = `+55 ${parsed?.isPossible() ? parsed.nationalNumber : phone}`;

                code = 'BR';
            } else {
                const parsed = parsePhoneNumberFromString(phone);

                code = parsed?.country;

                if (!code && parsed)
                    code = `+${parsed.countryCallingCode}` as PhoneCode;
            }

        phone = formatIncompletePhoneNumber(phone);

        if (code === 'BR' && phone.length === 16)
            phone = phone.replace(/([0-9]{4})([0-9]) /, '$1 $2')

        return { value: phone, code: code };
    }
    
    change(phone: string) {
        const parsed = this.parseValueAsPhone(phone);

        this.setState(parsed);

        this.props.onChange?.(parsed.value);
    }

    changeCode(code: string) {
        this.setState(prevState => {
            const phone = this.parseValueAsPhone(prevState.value, code as PhoneCode);

            this.props.onChange?.(phone.value);

            return phone;
        });
    }

    render() {
        const { whatsapp, value, hint, onChange, ...props } = this.props;

        return (
            <div className={`brax-phone-field ${!this.state.code ? 'code-invalid' : hint ? 'with-hint' : ''}`}>
                <BraxUncontrolledSelectField
                    label={t('País')}
                    name="phone-country"
                    value={this.state.code}
                    options={this.codesOptions}
                    status={this.state.code ? 'success' : 'error'}
                    variant="complete-value-as-title"
                    required
                    onChange={value => this.changeCode(value)}
                />

                <BraxUncontrolledTextField
                    value={this.state.value}
                    maxLength={this.state.code === 'BR' ? 17 : undefined}
                    onChange={value => this.change(value)}
                    hint={!this.state.code ? t('Código do país é obrigatório.') : hint}
                    {...props}
                />

                { this.props.whatsapp ? (
                    typeof this.props.whatsapp === 'function' ? (
                        this.props.whatsapp()
                    ) : (
                        <BraxUncontrolledCheckboxField
                            label={t('É WhatsApp?')}
                            {...(this.props.whatsapp !== true ? this.props.whatsapp : {} as {})}
                        />
                    )
                ) : null }
            </div>
        );
    }

}
